import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    CircularProgress,
    Tooltip,
    IconButton,
} from '@mui/material';
import {
    Add, 
    Edit,
    Delete,
    ArrowBackIosNew,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
// import { buildPermissionMap } from 'state/checkPermission';
import { buildGridColumns } from 'components/dynamicUI/buildGridColumns';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DataGridComponent from 'components/DataGridComponent';
import DeleteConfirm from 'components/DeleteConfirm';
import DynamicCard from './DynamicCard';


const DynamicList = () => {
    useAuthCheck();
    const { collection } = useParams();
    const navigate = useNavigate();
    const theme = useTheme(); 
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // const user = useSelector((state) => state.user);
    // const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
    // const { canCreate, canEdit, canDelete } = useMemo(() => {
    //     const specific = permissionMap.get(collection?.toLowerCase());
    //     const generic  = permissionMap.get('modules');
    //     const e = specific || generic;
    //     return {
    //         canCreate: e?.has('create') ?? false,
    //         canEdit:   e?.has('update') ?? false,
    //         canDelete: e?.has('delete') ?? false,
    //     };
    // }, [permissionMap, collection]);
    
    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [collectionDef, setCollectionDef] = useState(null);
    const [fields, setFields] = useState([]);
    const [rows, setRows] = useState([]);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

    useEffect(() => {
        const loadCollection = async () => {
            setLoading(true);
            setError(null);

            let collDef;
            try {
                const { data } = await api.get(`/collectiondefs/by-name/${collection}`);
                collDef = data.collectionDef;
                setCollectionDef(collDef);
                setFields(data.fields);
            } catch (err) {
                setError(err);
                toast.error(err.response?.data?.message || 'Failed to load collection!');
                setLoading(false);
                return;
            }

            try {
                const res = await api.get(`/data/${collDef.name}`);
                const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
                setRows(list);
            } catch (err) {
                toast.error(err.response?.data?.message || 'Failed to load data!');
            } finally {
                setLoading(false);
            }
        };

        loadCollection();
    }, [collection]);

    const dynamicColumns = useMemo(() => buildGridColumns(fields), [fields]);

    const handleDelete = useCallback(async () => {
        if (!deleteTarget || !collectionDef) return;
        try {
            await api.delete(`/data/${collectionDef.name}/${deleteTarget._id}`);
            setRows(prev => prev.filter(r => r._id !== deleteTarget._id));
            toast.success('Deleted successfully.');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Delete failed.');
        } finally {
            setDeleteTarget(null);
        }
    }, [deleteTarget, collectionDef]);

    const actionColumn = useMemo(() => ({
        field: '_actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={1}
                sx={{ width: "100%", height: "100%" }}
            >
                {/* {canEdit && ( */}
                    <Tooltip title="Edit" arrow>
                        <IconButton
                            onClick={() => navigate(`/modules/${collection}/${params.row._id}`)}
                        >
                            <Edit />
                        </IconButton>
                    </Tooltip>
                {/* )} */}
                {/* {canDelete && ( */}
                    <Tooltip title="Delete" arrow>
                        <IconButton
                            color="error"
                            onClick={() => setDeleteTarget(params.row)}
                        >
                            <Delete />
                        </IconButton>
                    </Tooltip>
                {/* )} */}
            </Box>
        ),
    }), [
        collection, 
        navigate, 
        // canEdit, 
        // canDelete
    ]);

    const columns = useMemo(
        () => [...dynamicColumns, actionColumn],
        [dynamicColumns, actionColumn]
    );

    if (loading) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <CircularProgress />
        </FlexCenter>
    );

    if (error) return (
        <FlexCenter height={LAYOUT_HEIGHT}>
            <Typography color="error">{error?.message}</Typography>
        </FlexCenter>
    );


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title={collectionDef?.label || collection}
                    subtitle={collectionDef?.description || `List of ${rows.length} Records`}
                />
                <Box display="flex" alignItems="center" gap={1}>                    
                    {/* {canCreate && ( */}
                        <Tooltip title="Create" arrow>
                            <span>
                                <StyledButton onClick={() => navigate(`/modules/${collection}/new`)}>
                                    <Add />
                                </StyledButton>
                            </span>
                        </Tooltip>
                        <StyledButton onClick={() => navigate('/collections')}>
                            <ArrowBackIosNew />
                        </StyledButton>
                    {/* )} */}
                </Box>
            </FlexBetween>

            <Box
                display="grid"
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                gap={2}
            >
                <Box
                    ref={scrollRef}
                    height={LAYOUT_HEIGHT}
                    sx={{ gridColumn: "span 12", overflowY: 'auto' }}
                >
                    {isMobile ? (
                        <DynamicCard
                            rows={rows}
                            fields={fields}
                            onEdit={(row) => navigate(`/modules/${collection}/${row._id}`)}
                            onDelete={setDeleteTarget}
                        />
                    ) : (
                        <DataGridComponent
                            rows={rows}
                            columns={columns}
                            columnVisibilityModel={columnVisibilityModel}
                            setColumnVisibilityModel={setColumnVisibilityModel}
                            loading={loading}
                        />
                    )}

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            </Box>

            <DeleteConfirm
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                deleteType="single"
            />
        </Box>
    );
};

export default DynamicList;
